<template>
  <div>
    <v-container fluid class="d-flex justify-center">
      <div style="width: 100%; max-width: 1200px;">

        <!-- ─── Sticky header bar ─── -->
        <div class="table-header-filters pa-4 mb-6">
          <v-row dense align="center" no-gutters style="gap: 12px">

            <v-col cols="auto">
              <span class="section-title">Uvoz artikala</span>
            </v-col>

            <!-- Separator + header toggle (shown once file is loaded) -->
            <template v-if="step < 3 && (step === 2 || rawText)">
              <v-col v-if="!xlsxMode" cols="auto" class="d-flex align-center" style="gap: 8px">
                <span class="caption grey--text">Separator:</span>
                <div class="sep-pills">
                  <button
                    v-for="sep in separatorOptions"
                    :key="sep.value"
                    class="sep-pill"
                    :class="{ 'sep-pill--active': separator === sep.value }"
                    @click="separator = sep.value"
                  >{{ sep.label }}</button>
                </div>
              </v-col>
              <v-col cols="auto">
                <v-checkbox
                  v-model="hasHeader"
                  label="Prvi redak je zaglavlje"
                  dense
                  hide-details
                  color="#00b3be"
                  class="mt-0 pt-0"
                />
              </v-col>
            </template>

            <v-col cols="auto" class="ml-auto">
              <v-btn v-if="step === 2" class="close-btn" @click="resetAll">
                <v-icon small left>mdi-arrow-left</v-icon>
                Nova datoteka
              </v-btn>
            </v-col>

          </v-row>
        </div>

        <!-- ─── Info banner ─── -->
        <div class="mb-6">
          <div v-if="!showHelp" class="d-flex justify-end">
            <button class="help-toggle-btn" @click="showHelp = true">
              <v-icon size="15" color="#0891B2" class="mr-1">mdi-help-circle-outline</v-icon>
              Upute za uvoz
            </button>
          </div>

          <div v-if="showHelp" class="info-banner">
            <v-btn icon x-small class="info-banner__close" @click="showHelp = false">
              <v-icon size="16">mdi-close</v-icon>
            </v-btn>
            <p class="info-banner__intro">
              Uvezite artikle iz CSV ili Excel datoteke u nekoliko koraka — odaberite datoteku, mapirajte kolone i pokrenite uvoz.
            </p>
            <div class="info-banner__grid">
              <div class="info-legend-item">
                <v-icon size="16" color="#0891B2">mdi-file-upload-outline</v-icon>
                <span><strong>Datoteka</strong> — odaberite CSV ili Excel (.xlsx) datoteku s popisom artikala.</span>
              </div>
              <div class="info-legend-item">
                <v-icon size="16" color="#0891B2">mdi-format-columns</v-icon>
                <span><strong>Separator</strong> — automatski se detektira iz CSV-a; promijenite ručno ako pregled ne izgleda ispravno.</span>
              </div>
              <div class="info-legend-item">
                <v-icon size="16" color="#0891B2">mdi-table-row</v-icon>
                <span><strong>Zaglavlje</strong> — označite ako prvi redak sadrži nazive kolona, inače će biti uvezen kao podatak.</span>
              </div>
              <div class="info-legend-item">
                <v-icon size="16" color="#0891B2">mdi-table-edit</v-icon>
                <span><strong>Mapiranje</strong> — dodijelite kolonama odgovarajuća polja; <strong>Naziv, Kategorija, Jedinica mjere i PDV</strong> su obavezna.</span>
              </div>
              <div class="info-legend-item" style="grid-column: 1 / -1">
                <v-icon size="16" color="#0891B2">mdi-barcode</v-icon>
                <span>
                  <strong>Šifra</strong> — šifre kraće od 3 znaka dopunjuju se nulama s lijeva (npr. <em>5</em> → <em>005</em>).
                  U slučaju duplikata: <strong>sufiks</strong> mod dodaje redni broj na kraj
                  (<em>005</em> → <em>005-1</em> → <em>005-2</em>…);
                  <strong>prefiks</strong> mod dodaje nule s lijeva
                  (<em>005</em> → <em>0005</em> → <em>00005</em>…).
                  Bez šifre u CSV-u, sustav je dodjeljuje automatski.
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- ─── KORAK 1: Odabir datoteke ─── -->
        <template v-if="step === 1">
          <v-card outlined rounded="lg" class="pa-6 mb-6">
            <div class="section-title mb-5">
              <v-icon color="#00b3be" class="mr-1" style="vertical-align: middle">mdi-table-arrow-up</v-icon>
              Postavke uvoza
            </div>
            <v-row>
              <v-col cols="12" sm="6">
                <v-select
                  v-model="selectedLocation"
                  :items="locations"
                  label="Prodajno mjesto"
                  class="filter-select"
                  outlined
                  dense
                  clearable
                  item-text="name"
                  item-value="id"
                >
                  <template v-slot:prepend-inner>
                    <v-icon small color="#00b3be" class="mt-1 mr-1">mdi-store-outline</v-icon>
                  </template>
                </v-select>
              </v-col>
              <v-col cols="12" sm="6">
                <v-select
                  v-model="sifre"
                  :items="sifreOptions"
                  label="Format šifre artikla"
                  class="filter-select"
                  outlined
                  dense
                >
                  <template v-slot:prepend-inner>
                    <v-icon small color="#9C27B0" class="mt-1 mr-1">mdi-tag-outline</v-icon>
                  </template>
                </v-select>
              </v-col>
              <v-col cols="12">
                <v-file-input
                  label="CSV ili Excel datoteka"
                  accept=".csv,.xlsx,.xls"
                  class="filter-select"
                  outlined
                  dense
                  prepend-icon=""
                  :disabled="uploading"
                  @change="onFileSelected"
                >
                  <template v-slot:prepend-inner>
                    <v-icon small color="#3989C9" class="mt-1 mr-1">mdi-file-upload-outline</v-icon>
                  </template>
                </v-file-input>
              </v-col>
            </v-row>

            <div v-if="uploading" class="mt-1">
              <div class="caption grey--text mb-1">Uploading… {{ Math.round(uploadProgress) }}%</div>
              <v-progress-linear :value="uploadProgress" color="#00b3be" rounded height="6" />
            </div>
          </v-card>
        </template>

        <!-- ─── KORAK 2: Preview + Column mapping ─── -->
        <template v-if="step === 2">
          <div class="glass-table-wrapper mb-6">
            <div class="section-title mb-4">
              <v-icon color="#3989C9" class="mr-1" style="vertical-align: middle">mdi-table-edit</v-icon>
              Mapiranje kolona
              <span class="caption grey--text font-weight-regular ml-2">— prvih {{ previewRows.length }} redaka</span>
            </div>

            <!-- Header check banner — unskippable, locks everything below -->
            <div v-if="headerPrompt" class="header-check-banner mb-4">
              <div class="header-check-banner__icon">
                <v-icon color="#92400E" size="20">mdi-alert</v-icon>
              </div>
              <div class="header-check-banner__body">
                <p class="header-check-banner__text">
                  <strong>VAŽNO:</strong> Pogledajte označeni prvi redak u tablici.
                  Sadrži li on nazive kolona (zaglavlje) ili stvarni artikl?
                  <span class="header-check-banner__note">(Krivi uvoz se ne može naknadno obrisati!)</span>
                </p>
                <div class="header-check-banner__actions">
                  <button class="hcb-btn hcb-btn--yes" @click="confirmHeader">
                    <v-icon x-small left class="mr-1">mdi-check</v-icon>
                    Da, to je zaglavlje
                  </button>
                  <button class="hcb-btn hcb-btn--no" @click="dismissHeader">
                    <v-icon x-small left class="mr-1">mdi-close</v-icon>
                    Ne, to je artikl
                  </button>
                </div>
              </div>
            </div>

            <div class="mapping-scroll" :class="{ 'mapping-scroll--locked': headerPrompt }">
              <table class="mapping-table">
                <thead>
                  <tr>
                    <th
                      v-for="(header, colIdx) in headers"
                      :key="'map-' + colIdx"
                      class="mapping-cell"
                    >
                      <div v-if="header" class="original-header-label">{{ header }}</div>
                      <v-select
                        :value="mapping[colIdx]"
                        :items="fieldOptionsForCol(colIdx)"
                        placeholder="— Preskoči —"
                        class="mapping-select"
                        dense
                        outlined
                        hide-details
                        clearable
                        :disabled="headerPrompt"
                        style="min-width: 140px"
                        :color="mapping[colIdx] ? (isRequired(mapping[colIdx]) ? '#3989C9' : '#00b3be') : ''"
                        @input="onMappingChange(colIdx, $event)"
                      >
                        <template v-slot:item="{ item, on, attrs }">
                          <v-list-item v-bind="attrs" v-on="on" dense>
                            <v-list-item-content>
                              <v-list-item-title
                                :style="item.alreadyMapped ? 'color: #94A3B8' : ''"
                              >{{ item.text }}</v-list-item-title>
                            </v-list-item-content>
                            <v-icon v-if="item.alreadyMapped" x-small color="#00b3be" class="ml-2">mdi-check-circle-outline</v-icon>
                          </v-list-item>
                        </template>
                      </v-select>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(row, rowIdx) in previewRows"
                    :key="rowIdx"
                    class="data-row"
                    :class="{ 'header-candidate': headerPrompt && rowIdx === 0 }"
                  >
                    <td v-for="(cell, colIdx) in row" :key="colIdx" class="data-cell">
                      {{ cell }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <v-alert v-if="mappingError" type="error" dense text class="mt-4 mb-0">
              {{ mappingError }}
            </v-alert>

            <div class="d-flex justify-end mt-5">
              <v-btn
                class="import-btn"
                :loading="submitting"
                :disabled="submitting || headerPrompt"
                @click="submit"
              >
                <v-icon left small>mdi-database-import-outline</v-icon>
                Uvezi
              </v-btn>
            </div>
          </div>
        </template>

        <!-- ─── KORAK 3: Praćenje importa ─── -->
        <template v-if="step === 3">
          <v-card outlined rounded="lg" class="pa-6">
            <div class="section-title mb-5">
              <v-icon color="#00b3be" class="mr-1" style="vertical-align: middle">mdi-database-import-outline</v-icon>
              {{ jobDone ? 'Uvoz završen' : 'Uvoz u tijeku' }}
            </div>

            <div class="d-flex align-center mb-5">
              <v-progress-circular v-if="!jobDone" indeterminate color="#00b3be" size="24" width="3" class="mr-3" />
              <v-icon v-else-if="jobStatus === 'done'" color="success" class="mr-2">mdi-check-circle</v-icon>
              <v-icon v-else-if="jobStatus === 'done_with_errors'" color="warning" class="mr-2">mdi-alert-circle</v-icon>
              <v-icon v-else-if="jobStatus === 'failed'" color="error" class="mr-2">mdi-close-circle</v-icon>
              <span class="body-2">{{ jobStatusText }}</span>
            </div>

            <div v-if="jobTotal > 0" class="mb-5">
              <div class="d-flex justify-space-between caption grey--text mb-1">
                <span>{{ jobSuccessful }} / {{ jobTotal }} artikala</span>
                <span>{{ Math.round((jobSuccessful / jobTotal) * 100) }}%</span>
              </div>
              <v-progress-linear
                :value="jobTotal > 0 ? (jobSuccessful / jobTotal) * 100 : 0"
                color="#00b3be"
                rounded
                height="6"
              />
            </div>

            <div v-if="jobErrors.length > 0" class="mt-2">
              <div class="caption font-weight-medium mb-2" style="color: #E65100">
                {{ jobErrors.length }} neuvezenih artikala:
              </div>
              <div class="job-errors-list">
                <div v-for="(err, i) in jobErrors" :key="i" class="job-error-item">
                  <v-icon size="14" color="#E65100" class="mr-1" style="flex-shrink:0">mdi-alert-outline</v-icon>
                  <span>{{ err }}</span>
                </div>
              </div>
            </div>

            <div v-if="jobDone" class="d-flex justify-end mt-6">
              <v-btn text small @click="resetAll">Novi uvoz</v-btn>
            </div>
          </v-card>
        </template>

      </div>
    </v-container>

    <!-- Conflict dialog -->
    <v-dialog v-model="conflictDialog" max-width="420">
      <v-card rounded="lg">
        <v-card-title class="section-title pa-5">Polje već mapirano</v-card-title>
        <v-card-text class="px-5">
          Polje <strong>{{ conflictField }}</strong> već je odabrano na koloni
          <strong>{{ conflictFromCol + 1 }}</strong>. Premjestiti?
        </v-card-text>
        <v-card-actions class="pa-5 pt-0">
          <v-spacer />
          <v-btn text small @click="cancelConflict">Odustani</v-btn>
          <v-btn class="import-btn" small @click="confirmConflict">Premjesti</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Invalid rows dialog -->
    <v-dialog v-model="showErrorDialog" max-width="600" scrollable>
      <v-card rounded="lg">
        <v-card-title class="section-title pa-5">Greške u datoteci</v-card-title>
        <v-card-text class="px-5">
          Datoteka sadrži neispravne retke — neispravni artikli neće biti upisani.
        </v-card-text>
        <v-card-text class="px-5 pt-0">
          <v-card
            v-for="(inRow, i) in invalidRows"
            :key="i"
            class="mb-2"
            outlined
          >
            <v-card-text class="py-2" v-html="inRow" />
          </v-card>
        </v-card-text>
        <v-card-actions class="pa-5 pt-0">
          <v-spacer />
          <v-btn text small @click="cancelImport">Zatvori</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from 'axios'
import * as XLSX from 'xlsx'
import { df, auth, storage } from '@/plugins/firebase'
import { getFirebaseIdToken } from '@/api/token'
import { v4 as uuidv4 } from 'uuid'
import state from '@/state'
import { clone } from '@/plugins/utils'

const FIELDS = ['Sifra', 'Naziv', 'MPC', 'JM', 'Naknada', 'Kategorija', 'Barcode', 'Kolicina', 'PDV']
const REQUIRED_FIELDS = ['Naziv', 'Kategorija', 'JM', 'PDV']
const FIELD_LABELS = {
  Sifra: 'Šifra',
  Naziv: 'Naziv',
  MPC: 'Cijena s PDV-om',
  JM: 'Jedinica mjere',
  Naknada: 'Naknada',
  Kategorija: 'Kategorija',
  Barcode: 'Barcode',
  Kolicina: 'Količina',
  PDV: 'PDV',
}

const AUTO_MAP = {
  Sifra: ['sifra', 'šifra', 'code', 'kod', 'item_code', 'artcode', 'sifraart'],
  Naziv: ['naziv', 'name', 'ime', 'opis', 'description', 'artikl'],
  MPC: ['mpc', 'cijena', 'price', 'mj.prod.cijena', 'prodajna', 'maloprodajna'],
  JM: ['jm', 'jedinica', 'unit', 'jed.mjere', 'jedinicamjere'],
  Naknada: ['naknada', 'fee', 'packaging', 'povratna'],
  Kategorija: ['kategorija', 'category', 'grupa', 'group', 'grp'],
  Barcode: ['barcode', 'barkod', 'ean', 'upc', 'bar_code'],
  Kolicina: ['kolicina', 'količina', 'qty', 'quantity', 'zaliha', 'stock'],
  PDV: ['pdv', 'tax', 'porez', 'vat', 'stopa'],
}

function detectSeparator (firstLine) {
  const candidates = [',', ';', '\t', '|']
  return candidates.reduce((best, sep) =>
    firstLine.split(sep).length > firstLine.split(best).length ? sep : best
  , ',')
}

function parseCSV (text, separator, maxRows = 11) {
  return text.split('\n')
    .slice(0, maxRows)
    .map(row => row.split(separator).map(c => c.trim().replace(/^"|"$/g, '')))
    .filter(row => row.some(c => c !== ''))
}

function autoMap (headers) {
  const mapping = new Array(headers.length).fill(null)
  const used = new Set()
  headers.forEach((header, idx) => {
    const normalized = header.toLowerCase().replace(/\s/g, '')
    for (const [field, aliases] of Object.entries(AUTO_MAP)) {
      if (!used.has(field) && aliases.some(a => normalized.includes(a))) {
        mapping[idx] = field
        used.add(field)
        break
      }
    }
  })
  return mapping
}

export default {
  name: 'UploadItems',
  inject: ['showMsgBox', 'showLoader', 'hideLoader'],

  data () {
    return {
      company: null,
      locations: [],
      selectedLocation: '',
      step: 1,
      showHelp: false,

      separatorOptions: [
        { value: ',', label: ', zarez' },
        { value: ';', label: '; točka-zarez' },
        { value: '\t', label: 'tab' },
        { value: '|', label: '| crta' },
      ],
      sifreOptions: [
        { text: 'Sufiks — u slučaju duplikata: 005, 005-1, 005-2…', value: 'suffix' },
        { text: 'Prefiks — u slučaju duplikata: 005, 0005, 00005…', value: 'prefix' },
      ],

      // File + separator
      csvFile: null,
      separator: ',',
      separatorDetected: false,
      hasHeader: false,
      headerPrompt: false,
      rawText: '',
      xlsxMode: false,
      xlsxRows: null,

      // Import options
      sifre: 'suffix',

      // GCS upload
      uploading: false,
      uploadProgress: 0,
      gcsPath: '',

      // Preview + mapping
      headers: [],
      previewRows: [],
      mapping: [],
      mappingError: '',

      // Conflict dialog
      conflictDialog: false,
      conflictField: '',
      conflictFromCol: -1,
      conflictToCol: -1,
      pendingMappingValue: null,

      // Submit
      submitting: false,

      // Error dialog
      showErrorDialog: false,
      invalidRows: [],

      // Job tracking
      jobId: '',
      jobStatus: '',
      jobTotal: 0,
      jobSuccessful: 0,
      jobErrors: [],
      jobUnsubscribe: null,
    }
  },

  computed: {
    jobDone () {
      return ['done', 'done_with_errors', 'failed'].includes(this.jobStatus)
    },
    jobStatusText () {
      switch (this.jobStatus) {
        case 'queued': return 'U redu čekanja...'
        case 'processing': return this.jobSuccessful > 0
          ? `Uvoz u tijeku — obrađeno ${this.jobSuccessful} od ${this.jobTotal} artikala`
          : 'Uvoz u tijeku...'
        case 'done': return `Završeno — ${this.jobSuccessful} artikala uspješno uvezeno`
        case 'done_with_errors': return `Završeno s greškama — ${this.jobSuccessful} uspješno, ${this.jobErrors.length} neuvezenih`
        case 'failed': return 'Uvoz neuspješan'
        default: return 'Pokretanje...'
      }
    },
  },

  watch: {
    separator () {
      if (this.rawText) this.parseAndShow()
    },
    hasHeader () {
      this.mapping = []
      if (this.rawText) this.parseAndShow()
    }
  },

  mounted () {
    this.company = clone(state.getCurrentCompany())
    this.loadLocations()
  },

  beforeDestroy () {
    this.stopJobTracking()
  },

  methods: {
    isRequired (field) {
      return REQUIRED_FIELDS.includes(field)
    },

    confirmHeader () {
      this.headerPrompt = false
      this.hasHeader = true
    },

    dismissHeader () {
      this.headerPrompt = false
    },

    fieldOptionsForCol (colIdx) {
      const mappedElsewhere = new Set(
        this.mapping.filter((f, i) => i !== colIdx && f)
      )
      const build = (fields) => fields
        .sort((a, b) => FIELD_LABELS[a].localeCompare(FIELD_LABELS[b], 'hr'))
        .map(f => ({
          text: FIELD_LABELS[f] + (REQUIRED_FIELDS.includes(f) ? ' *' : ''),
          value: f,
          alreadyMapped: mappedElsewhere.has(f),
        }))
      return [
        { text: '— Preskoči —', value: null, alreadyMapped: false },
        ...build(FIELDS.filter(f => REQUIRED_FIELDS.includes(f))),
        ...build(FIELDS.filter(f => !REQUIRED_FIELDS.includes(f))),
      ]
    },

    loadLocations () {
      df.doc(`user_locations/${auth.currentUser.uid}.${this.company.id}`)
        .get()
        .then(doc => {
          if (doc.data()?.locations) {
            this.locations = Object.values(doc.data().locations)
              .filter(l => l.status === 'OK')
            if (this.locations.length > 0) {
              this.selectedLocation = this.locations[0].id
            }
          }
        })
    },

    onFileSelected (file) {
      if (!file) {
        this.rawText = ''
        this.csvFile = null
        return
      }
      this.csvFile = file

      const ext = file.name.split('.').pop().toLowerCase()

      if (ext === 'xlsx' || ext === 'xls') {
        const reader = new FileReader()
        reader.onload = e => {
          const wb = XLSX.read(e.target.result, { type: 'array' })
          const sheet = wb.Sheets[wb.SheetNames[0]]
          // Store rows for preview (array of arrays)
          this.xlsxRows = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' })
          // Convert to CSV string for GCS upload
          this.rawText = XLSX.utils.sheet_to_csv(sheet, { FS: this.separator })
          this.xlsxMode = true
          this.parseAndShow()
          this.uploadXlsxAsCsv(file.name)
        }
        reader.readAsArrayBuffer(file)
      } else {
        const reader = new FileReader()
        reader.onload = e => {
          this.rawText = e.target.result
          this.xlsxMode = false
          this.xlsxRows = null

          if (!this.separatorDetected) {
            const firstLine = this.rawText.split('\n')[0] || ''
            this.separator = detectSeparator(firstLine)
            this.separatorDetected = true
          }

          this.parseAndShow()
          this.uploadToGCS(file)
        }
        reader.readAsText(file, 'UTF-8')
      }
    },

    parseAndShow () {
      let rows
      if (this.xlsxMode && this.xlsxRows) {
        rows = this.xlsxRows.filter(r => r.some(c => String(c) !== ''))
      } else {
        rows = parseCSV(this.rawText, this.separator)
      }
      if (rows.length === 0) return

      if (this.hasHeader) {
        this.headers = rows[0].map(c => String(c))
        this.previewRows = rows.slice(1, 11).map(r => r.map(c => String(c)))
      } else {
        const colCount = rows[0].length
        this.headers = Array.from({ length: colCount }, (_, i) => `Kolona ${i + 1}`)
        this.previewRows = rows.slice(0, 10).map(r => r.map(c => String(c)))
      }

      if (this.mapping.length === 0 || this.mapping.length !== this.headers.length) {
        this.mapping = autoMap(this.headers)
      }
    },

    uploadXlsxAsCsv (originalName) {
      this.uploading = true
      this.uploadProgress = 0
      const baseName = originalName.replace(/\.[^.]+$/, '') + '.csv'
      this.gcsPath = `imports/${this.company.id}/${uuidv4()}/${baseName}`

      const blob = new Blob([this.rawText], { type: 'text/csv' })
      const uploadTask = storage.ref(this.gcsPath).put(blob)
      uploadTask.on(
        'state_changed',
        snap => { this.uploadProgress = (snap.bytesTransferred / snap.totalBytes) * 100 },
        err => {
          this.uploading = false
          this.showMsgBox({ text: 'Greška pri uploadu: ' + err.message, actions: ['cancel'], cancelBtnText: 'OK', color: 'error' })
        },
        () => { this.uploading = false; this.step = 2; this.headerPrompt = true }
      )
    },

    uploadToGCS (file) {
      this.uploading = true
      this.uploadProgress = 0
      this.gcsPath = `imports/${this.company.id}/${uuidv4()}/${file.name}`

      const uploadTask = storage.ref(this.gcsPath).put(file)
      uploadTask.on(
        'state_changed',
        snap => { this.uploadProgress = (snap.bytesTransferred / snap.totalBytes) * 100 },
        err => {
          this.uploading = false
          this.showMsgBox({ text: 'Greška pri uploadu: ' + err.message, actions: ['cancel'], cancelBtnText: 'OK', color: 'error' })
        },
        () => { this.uploading = false; this.step = 2; this.headerPrompt = true }
      )
    },

    onMappingChange (colIdx, newField) {
      const existingIdx = newField
        ? this.mapping.findIndex((f, i) => i !== colIdx && f === newField)
        : -1

      if (existingIdx !== -1) {
        // Conflict — ask user
        this.conflictField = newField
        this.conflictFromCol = existingIdx
        this.conflictToCol = colIdx
        this.pendingMappingValue = newField
        this.conflictDialog = true
        return
      }

      this.$set(this.mapping, colIdx, newField || null)
      this.mappingError = ''
    },

    confirmConflict () {
      this.$set(this.mapping, this.conflictFromCol, null)
      this.$set(this.mapping, this.conflictToCol, this.pendingMappingValue)
      this.conflictDialog = false
      this.mappingError = ''
    },

    cancelConflict () {
      this.conflictDialog = false
    },

    buildCols () {
      const cols = {}
      this.mapping.forEach((field, idx) => {
        if (field) cols[field.toLowerCase()] = idx
      })
      return cols
    },

    async submit () {
      this.mappingError = ''

      const missing = REQUIRED_FIELDS.filter(f => !this.mapping.includes(f))
      if (missing.length > 0) {
        this.mappingError = `Obavezna polja nisu mapirana: ${missing.map(f => FIELD_LABELS[f]).join(', ')}`
        return
      }

      this.submitting = true
      this.showLoader()

      try {
        const token = await getFirebaseIdToken()
        const bucket = process.env.VUE_APP_FIREBASE_STORAGE_BUCKET
        const apiUrl = process.env.VUE_APP_API_ROOT + 'csv-import-api'

        const payload = {
          company_id: this.company.id,
          location_id: this.selectedLocation,
          warehouses: this.selectedLocation ? [this.selectedLocation] : [],
          csv_path: `gs://${bucket}/${this.gcsPath}`,
          delimiter: this.separator,
          sifre: this.sifre,
          cols: this.buildCols()
        }

        const response = await axios.post(apiUrl, payload, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        })

        this.hideLoader()
        this.submitting = false

        const data = response.data

        if (data?.job_id) {
          this.jobId = data.job_id
          this.jobStatus = 'queued'
          this.step = 3
          this.startJobTracking(data.job_id)
          return
        }

        // fallback: stari sync odgovor
        if (data?.invalid_rows) {
          const invalidRows = data.invalid_rows
          this.invalidRows = Object.keys(invalidRows).map(key => {
            let text = `Redak broj ${key}:<br>`
            Object.values(invalidRows[key]).forEach(msg => { text += msg + '<br>' })
            return text
          })
          this.showErrorDialog = true
          return
        }

        this.showMsgBox({
          text: 'Artikli su uspješno uvezeni!',
          actions: ['cancel'],
          cancelBtnText: 'Zatvori',
          color: 'success'
        })
        this.resetAll()
      } catch (err) {
        this.hideLoader()
        this.submitting = false
        const msg = err?.response?.data?.message || err?.message || 'Greška pri uvozu.'
        this.showMsgBox({
          text: msg,
          actions: ['cancel'],
          cancelBtnText: 'OK',
          color: 'error'
        })
      }
    },

    cancelImport () {
      this.showErrorDialog = false
      this.invalidRows = []
    },

    startJobTracking (jobId) {
      this.jobUnsubscribe = df.doc(`csv_imports/${jobId}`).onSnapshot(snapshot => {
        const data = snapshot.data()
        if (!data) return
        this.jobStatus = data.status || ''
        this.jobTotal = data.total_items || 0
        this.jobSuccessful = data.successful || 0
        this.jobErrors = Array.isArray(data.errors) ? data.errors : []
      })
    },

    stopJobTracking () {
      if (this.jobUnsubscribe) {
        this.jobUnsubscribe()
        this.jobUnsubscribe = null
      }
    },

    resetAll () {
      this.step = 1
      this.csvFile = null
      this.rawText = ''
      this.separatorDetected = false
      this.separator = ','
      this.hasHeader = false
      this.headerPrompt = false
      this.xlsxMode = false
      this.xlsxRows = null
      this.uploading = false
      this.uploadProgress = 0
      this.gcsPath = ''
      this.headers = []
      this.previewRows = []
      this.mapping = []
      this.mappingError = ''
      this.submitting = false
      this.invalidRows = []
      this.showErrorDialog = false
      this.stopJobTracking()
      this.jobId = ''
      this.jobStatus = ''
      this.jobTotal = 0
      this.jobSuccessful = 0
      this.jobErrors = []
    }
  }
}
</script>

<style scoped>
/* ── Layout ── */
.table-header-filters {
  position: sticky;
  top: 64px;
  z-index: 10;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}

.section-title {
  font-size: 16px;
  font-weight: 700;
  color: #2d3748;
}

/* ── Filter selects ── */
.filter-select.v-text-field--outlined >>> fieldset {
  border-color: rgba(57, 137, 201, 0.2) !important;
  border-radius: 8px !important;
}
.filter-select.v-text-field--outlined:hover >>> fieldset {
  border-color: rgba(57, 137, 201, 0.4) !important;
}
.filter-select.v-text-field--outlined.v-input--is-focused >>> fieldset {
  border-color: #3989C9 !important;
  border-width: 2px !important;
}
.filter-select >>> .v-input__slot {
  background: rgba(255, 255, 255, 0.8) !important;
  min-height: 36px !important;
  border-radius: 8px !important;
}
.filter-select >>> .v-select__selection,
.filter-select >>> .v-text-field__slot input {
  font-size: 13px !important;
  font-weight: 500 !important;
  color: rgba(70, 70, 80, 0.9) !important;
  letter-spacing: 0.3px !important;
}
.filter-select >>> label {
  font-size: 12px !important;
  font-weight: 500 !important;
  color: rgba(57, 137, 201, 0.8) !important;
}

/* ── Mapping selects (inside table header) ── */
.mapping-select.v-text-field--outlined >>> fieldset {
  border-color: rgba(0, 179, 190, 0.2) !important;
  border-radius: 8px !important;
}
.mapping-select.v-text-field--outlined:hover >>> fieldset {
  border-color: rgba(0, 179, 190, 0.45) !important;
}
.mapping-select.v-text-field--outlined.v-input--is-focused >>> fieldset {
  border-color: #00b3be !important;
  border-width: 2px !important;
}
.mapping-select >>> .v-input__slot {
  background: #ffffff !important;
  min-height: 34px !important;
  border-radius: 8px !important;
}
.mapping-select >>> .v-select__selection {
  font-size: 12px !important;
  font-weight: 600 !important;
  color: #00838F !important;
  letter-spacing: 0.2px !important;
}
.mapping-select >>> .v-select__selections input {
  font-size: 12px !important;
}
.mapping-select >>> .v-input__append-inner .v-icon {
  font-size: 18px !important;
  color: rgba(0, 131, 143, 0.5) !important;
}

/* ── Back button ── */
.close-btn {
  height: 36px !important;
  min-width: unset !important;
  padding: 0 12px !important;
  border-radius: 10px !important;
  background: rgba(255, 255, 255, 0.95) !important;
  border: 1px solid rgba(229, 57, 53, 0.2) !important;
  color: #e53935 !important;
  transition: all 0.2s ease !important;
  box-shadow: none !important;
}
.close-btn:hover {
  background: rgba(229, 57, 53, 0.07) !important;
  box-shadow: 0 0 0 3px rgba(229, 57, 53, 0.1) !important;
}

/* ── Separator pills ── */
.sep-pills {
  display: flex;
  gap: 4px;
}
.sep-pill {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 16px;
  font-size: 12px;
  font-weight: 500;
  color: #6B7280;
  cursor: pointer;
  border: 1.5px solid #E2E8F0;
  background: #fff;
  transition: all 0.15s;
  outline: none;
  white-space: nowrap;
}
.sep-pill--active {
  border-color: rgba(0, 179, 190, 0.5) !important;
  color: #00838F !important;
  background: rgba(0, 188, 212, 0.06) !important;
}
.sep-pill:hover:not(.sep-pill--active) {
  border-color: rgba(0, 179, 190, 0.3);
  background: rgba(0, 188, 212, 0.03);
}

/* ── Import button ── */
.import-btn {
  height: 36px !important;
  padding: 0 18px !important;
  font-weight: 500 !important;
  font-size: 13px !important;
  text-transform: none !important;
  letter-spacing: 0.3px !important;
  background: rgba(0, 179, 190, 0.07) !important;
  color: #00838F !important;
  border: 1px solid rgba(0, 179, 190, 0.3) !important;
  border-radius: 8px !important;
  box-shadow: none !important;
  transition: all 0.2s ease !important;
}
.import-btn:hover:not(:disabled) {
  background: rgba(0, 179, 190, 0.14) !important;
  border-color: #00b3be !important;
  box-shadow: 0 2px 8px rgba(0, 179, 190, 0.2) !important;
}
.import-btn:disabled {
  opacity: 0.45 !important;
}

/* ── Glass table wrapper ── */
.glass-table-wrapper {
  background: #ffffff;
  border-radius: 16px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
  padding: 24px;
}

.mapping-scroll {
  overflow-x: scroll;
  padding-bottom: 4px;
}

.mapping-scroll::-webkit-scrollbar {
  height: 6px;
}
.mapping-scroll::-webkit-scrollbar-track {
  background: #EDF2F7;
  border-radius: 4px;
}
.mapping-scroll::-webkit-scrollbar-thumb {
  background: rgba(0, 179, 190, 0.4);
  border-radius: 4px;
}
.mapping-scroll::-webkit-scrollbar-thumb:hover {
  background: rgba(0, 179, 190, 0.7);
}

/* ── Mapping table ── */
.mapping-table {
  border-collapse: collapse;
  min-width: 100%;
  font-size: 13px;
}

.mapping-cell {
  padding: 12px 8px 8px;
  background: #F8FAFC;
  border-bottom: 2px solid #EDF2F7;
  vertical-align: top;
  min-width: 160px;
}

.original-header-label {
  font-weight: 700;
  font-size: 11px;
  color: #2D3748;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 160px;
  margin-bottom: 6px;
}

.data-cell {
  padding: 10px 8px;
  border-bottom: 1px solid #EDF2F7;
  white-space: nowrap;
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  color: #4A5568;
}

.data-row:last-child .data-cell {
  border-bottom: none;
}

.data-row:hover .data-cell {
  background: #F7FAFC;
}

/* ── Help toggle button ── */
.help-toggle-btn {
  display: inline-flex;
  align-items: center;
  padding: 5px 12px;
  border-radius: 16px;
  font-size: 12px;
  font-weight: 500;
  color: #0891B2;
  cursor: pointer;
  border: 1.5px solid rgba(8, 145, 178, 0.3);
  background: rgba(240, 249, 255, 0.8);
  transition: all 0.15s;
  outline: none;
}

.help-toggle-btn:hover {
  background: #E0F2FE;
  border-color: rgba(8, 145, 178, 0.6);
}

/* ── Info banner ── */
.info-banner {
  position: relative;
  background: #F0F9FF;
  border-left: 4px solid #0891B2;
  border-radius: 10px;
  padding: 18px 20px 16px;
}

.info-banner__close {
  position: absolute !important;
  top: 10px;
  right: 10px;
  color: #94A3B8 !important;
}

.info-banner__close:hover {
  color: #475569 !important;
}

.info-banner__intro {
  font-size: 13px;
  color: #334155;
  line-height: 1.6;
  margin: 0 0 14px;
  padding-right: 24px;
}

.info-banner__grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px 24px;
}

.info-legend-item {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 12.5px;
  color: #64748B;
  line-height: 1.5;
}

.info-legend-item strong {
  color: #1E293B;
}

.info-legend-item .v-icon {
  flex-shrink: 0;
  margin-top: 1px;
}

@media (max-width: 600px) {
  .info-banner__grid {
    grid-template-columns: 1fr;
  }
}

/* ── Job errors list ── */
.job-errors-list {
  max-height: 240px;
  overflow-y: auto;
  border: 1px solid #FED7D7;
  border-radius: 6px;
  background: #FFF5F5;
}

.job-error-item {
  padding: 8px 12px;
  font-size: 12.5px;
  color: #C53030;
  border-bottom: 1px solid #FED7D7;
  line-height: 1.5;
}

.job-error-item:last-child {
  border-bottom: none;
}

/* ── Header check banner ── */
.header-check-banner {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  background: #FFFBEB;
  border: 1.5px solid #F59E0B;
  border-radius: 10px;
  padding: 14px 16px;
}

.header-check-banner__icon {
  flex-shrink: 0;
  margin-top: 1px;
}

.header-check-banner__body {
  flex: 1;
}

.header-check-banner__text {
  font-size: 13px;
  color: #78350F;
  line-height: 1.5;
  margin: 0 0 10px;
}

.header-check-banner__note {
  font-size: 12px;
  color: #92400E;
  font-style: italic;
}

.header-check-banner__actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.hcb-btn {
  display: inline-flex;
  align-items: center;
  padding: 5px 14px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  border: 1.5px solid;
  transition: all 0.15s;
  outline: none;
}

.hcb-btn--yes {
  background: rgba(251, 191, 36, 0.2);
  border-color: #F59E0B;
  color: #78350F;
}

.hcb-btn--yes:hover {
  background: rgba(251, 191, 36, 0.35);
}

.hcb-btn--no {
  background: #fff;
  border-color: rgba(120, 53, 15, 0.25);
  color: #78350F;
}

.hcb-btn--no:hover {
  background: rgba(120, 53, 15, 0.06);
  border-color: rgba(120, 53, 15, 0.5);
}

/* ── Header candidate row ── */
.header-candidate .data-cell {
  background: rgba(251, 191, 36, 0.15) !important;
  border-bottom: 1px solid rgba(251, 191, 36, 0.4) !important;
}

/* ── Locked mapping table ── */
.mapping-scroll--locked {
  opacity: 0.5;
  pointer-events: none;
}
</style>
